import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bank, CheckCircle, CircleNotch, Users } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { apiGet, apiSend } from "@/lib/api-error";
import { formatCurrency, formatDateTimeBs } from "@/lib/format";
import { toast } from "@/lib/toast";

interface PendingTransferEnrollment {
  id: string;
  // 'failed': expired by the 7-day sweep, still confirmable until the cycle starts.
  paymentStatus: "pending_transfer" | "failed";
  clientName: string;
  clientEmail: string;
  cycleName: string;
  therapistUserName: string;
  priceCharged: number;
  bookingCountry: string | null;
  enrolledAt: string;
  firstSessionAt: string | null;
}

const QUERY_KEY = ["admin", "group-enrollments", "pending-transfer"] as const;

export function GroupEnrollmentTransfers() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState<PendingTransferEnrollment | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () =>
      apiGet<{ enrollments: PendingTransferEnrollment[] }>(
        "/api/group-therapies/admin/pending-transfers",
        t("adminGroupEnrollments.loadError")
      ).then((r) => r.enrollments),
  });

  const confirmMutation = useMutation({
    mutationFn: (id: string) =>
      apiSend(
        `/api/group-therapies/enrollments/${id}/confirm-payment`,
        "POST",
        undefined,
        t("adminGroupEnrollments.confirmError")
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setConfirming(null);
      toast.success(t("adminGroupEnrollments.confirmSuccess"));
    },
    onError: (err: Error) => {
      toast.error(err.message || t("adminGroupEnrollments.confirmError"));
    },
  });

  const enrollments = data ?? [];

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <header className="space-y-1">
        <h1
          className="text-2xl font-bold text-foreground"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {t("adminGroupEnrollments.title")}
        </h1>
        <p className="text-sm text-muted-foreground">{t("adminGroupEnrollments.subtitle")}</p>
      </header>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <CircleNotch className="size-6 animate-spin text-primary" />
        </div>
      ) : enrollments.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border/60 bg-card px-6 py-14 text-center">
          <CheckCircle className="size-9 text-emerald-600/50" weight="duotone" />
          <p className="mt-3 text-sm text-muted-foreground">
            {t("adminGroupEnrollments.empty")}
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {enrollments.map((e) => (
            <li
              key={e.id}
              className="rounded-2xl border border-border/60 bg-card p-4 shadow-sm sm:p-5"
            >
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 space-y-2">
                  <div className="flex items-center gap-2">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <Users className="size-4" weight="duotone" />
                    </div>
                    <div className="min-w-0">
                      <p className="flex items-center gap-2 truncate font-semibold text-foreground">
                        {e.clientName}
                        {e.paymentStatus === "failed" && (
                          <span
                            title={t("adminGroupEnrollments.expiredHint")}
                            className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
                          >
                            {t("adminGroupEnrollments.expired")}
                          </span>
                        )}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">{e.clientEmail}</p>
                    </div>
                  </div>
                  <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                    <Field label={t("adminGroupEnrollments.cycle")} value={e.cycleName} />
                    <Field
                      label={t("adminGroupEnrollments.therapist")}
                      value={e.therapistUserName}
                    />
                    <Field
                      label={t("adminGroupEnrollments.enrolledAt")}
                      value={formatDateTimeBs(e.enrolledAt)}
                    />
                    <Field
                      label={t("adminGroupEnrollments.firstSession")}
                      value={e.firstSessionAt ? formatDateTimeBs(e.firstSessionAt) : "-"}
                    />
                  </dl>
                </div>

                <div className="flex shrink-0 flex-col items-start gap-3 lg:items-end">
                  <div className="text-left lg:text-right">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground">
                      {t("adminGroupEnrollments.amount")}
                    </p>
                    <p
                      className="text-xl font-semibold tabular-nums text-foreground"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {formatCurrency(e.priceCharged)}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setConfirming(e)}
                    disabled={confirmMutation.isPending}
                    className="bg-emerald-600 text-white hover:bg-emerald-600/90"
                  >
                    <Bank className="size-4" weight="fill" />
                    {t("adminGroupEnrollments.confirm")}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ConfirmDialog
        open={confirming !== null}
        title={t("adminGroupEnrollments.confirmTitle")}
        description={t("adminGroupEnrollments.confirmBody")}
        confirmLabel={t("adminGroupEnrollments.confirm")}
        cancelLabel={t("adminGroupEnrollments.cancel")}
        isPending={confirmMutation.isPending}
        onClose={() => setConfirming(null)}
        onConfirm={() => confirming && confirmMutation.mutate(confirming.id)}
      />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="shrink-0 text-xs uppercase tracking-wide text-muted-foreground">{label}:</dt>
      <dd className="min-w-0 truncate font-medium text-foreground">{value}</dd>
    </div>
  );
}
